import { useParams, Outlet, useLocation, Link, useNavigate } from "react-router-dom";
import { useMemo } from "react";
import { KnowledgeCatalogTab } from "./types";
import { isTerminalStatus } from "./utils/statusHelpers";
import { useDocumentStatusNotification } from "./hooks/useDocumentStatusNotification";
import MetadataCard from "./components/MetadataCard";
import { LoadingButton, Popconfirm, Spinner, Stack } from "@altimateai/lego";
import { Button, Typography, toast, Alert, AlertDescription, AlertTitle } from "@altimateai/lego";
import { Tabs, TabsList, TabsTrigger } from "@altimateai/lego";
import { useCanEditPrivacyAware } from "@/helpers/privacyPermissions";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { deleteKnowledgeSource, getKnowledgeBaseWithDocumentStatus } from "@/helpers/apis";
import { formatDate, parseId } from "@/helpers/utils";
import { IconBook, IconBulb, IconHistory } from "@tabler/icons-react";
import { useTenantInfo, useUsers } from "@/helpers/hooks";
import KnowledgeCatalogDetailsV1 from "./KnowledgeCatalogDetailsV1";
import { useBreadcrumb } from "@/components/breadcrumbs";
import { KnowledgeTracking } from "@/lib/tracking/knowledge";

export default function KnowledgeCatalogDetailsV2() {
  const { id: rawId, templateName } = useParams();
  const id = parseId(rawId);
  const location = useLocation();
  const { enableKnowledgeEngine } = useTenantInfo();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getFormattedUserNameById } = useUsers();
  const canEditKnowledgeSource = useCanEditPrivacyAware();

  // Determine active tab from URL
  const activeTab = useMemo((): KnowledgeCatalogTab => {
    const path = location.pathname;
    if (path.includes("/learnings")) return KnowledgeCatalogTab.LEARNINGS;
    if (path.includes("/history")) return KnowledgeCatalogTab.HISTORY;
    return KnowledgeCatalogTab.DOCUMENT;
  }, [location.pathname]);

  // Polling interval for non-terminal statuses (30 seconds)
  const POLLING_INTERVAL_MS = 30 * 1000;

  // V2 API for knowledge bases with documents
  const {
    data,
    isLoading: isV2Loading,
    error: v2Error,
  } = useQuery({
    queryKey: ["knowledge-base-v2", id],
    queryFn: () => getKnowledgeBaseWithDocumentStatus(id!),
    enabled: !!id && !templateName,
    gcTime: 2 * 60 * 1000,
    // Poll every 30 seconds if document is still processing
    refetchInterval: query => {
      const status = query.state.data?.processing_status;
      // Poll if status exists and is not terminal
      if (status && !isTerminalStatus(status)) {
        return POLLING_INTERVAL_MS;
      }
      return false;
    },
  });

  // Show toast notification when document processing completes
  useDocumentStatusNotification({
    status: data?.processing_status,
    documentName: data?.name,
  });

  const { mutate, isPending: isDeleting } = useMutation({
    mutationFn: () => deleteKnowledgeSource(id!),
    onSuccess: () => {
      if (id) {
        KnowledgeTracking.sourceDeleted(id);
      }
      queryClient.invalidateQueries({ queryKey: ["knowledgeSource", id] });
      navigate("/knowledge-hub");
    },
    onError: () => {
      toast({
        title: "Failed to delete knowledge",
        description: "Please try again.",
        variant: "destructive",
      });
    },
  });

  // For templates, always use V1 view since they don't have documents
  if (templateName) {
    return <KnowledgeCatalogDetailsV1 />;
  }
  const createdDate = data?.created_at ? formatDate(data.created_at) : "N/A";
  const title = data?.name;

  // Returns null for service users, formatted name for regular users, or email if no name
  const getUserInfo = (userId?: number) => getFormattedUserNameById(userId, "email");

  useBreadcrumb(title ?? "Loading...", {
    defaultParents: [
      { label: "Datamates", pathname: "/datamates" },
      { label: "Knowledge Hub", pathname: "/knowledge-hub" },
    ],
  });

  return (
    <Stack className="al-p-6 al-gap-6" direction="column">
      {isV2Loading ? (
        <Spinner />
      ) : v2Error && !!id ? (
        <Alert variant="destructive">
          <AlertTitle>Failed to load knowledge base</AlertTitle>
          <AlertDescription>
            {(v2Error as Error)?.message ||
              "There was an error loading the knowledge base. Please try again later."}
          </AlertDescription>
        </Alert>
      ) : (
        <>
          <Stack direction="row" justify="between" align="center">
            <Typography variant="page-heading">{title}</Typography>
            {data && canEditKnowledgeSource(data) ? (
              <Stack>
                <Button asChild variant="outline">
                  <Link to={`/knowledge-hub/edit/${id}`}>
                    {enableKnowledgeEngine ? "Edit / Upload New Version" : "Edit"}
                  </Link>
                </Button>
                <Popconfirm
                  title="Are you sure?"
                  description="This action cannot be undone."
                  onConfirm={() => mutate()}
                >
                  <LoadingButton loading={isDeleting} variant="destructive">
                    Delete
                  </LoadingButton>
                </Popconfirm>
              </Stack>
            ) : null}
          </Stack>
          {/* Metadata Card */}
          <MetadataCard
            data={data}
            createdDate={createdDate}
            getUserInfo={getUserInfo}
            onLinkCopied={() => { if (id) KnowledgeTracking.linkCopied(id); }}
          />

          {/* Document/Chunks/Learnings/History Tabs */}
          <Tabs value={activeTab} className="al-w-full">
            <TabsList>
              <TabsTrigger value={KnowledgeCatalogTab.DOCUMENT} asChild>
                <Link to=".">
                  <IconBook className="al-w-4 al-h-4 al-mr-2" />
                  Document
                </Link>
              </TabsTrigger>
              <TabsTrigger value={KnowledgeCatalogTab.LEARNINGS} asChild>
                <Link to="learnings">
                  <IconBulb className="al-w-4 al-h-4 al-mr-2" />
                  Learnings
                </Link>
              </TabsTrigger>
              <TabsTrigger value={KnowledgeCatalogTab.HISTORY} asChild>
                <Link to="history">
                  <IconHistory className="al-w-4 al-h-4 al-mr-2" />
                  History
                </Link>
              </TabsTrigger>
            </TabsList>

            {/* Content area - rendered by nested routes via Outlet */}
            <div className="al-mt-4">
              <Outlet
                context={{
                  data,
                  kbId: id || undefined,
                  documentId: data?.current_document_id ?? undefined,
                  getUserInfo,
                }}
              />
            </div>
          </Tabs>
        </>
      )}
    </Stack>
  );
}
